import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import I18n from 'i18n-js';
import PropTypes from 'prop-types';
import { enableShopifyOrdersSelector } from 'selectors/app-selector';
import { ROUTES_V2_PATHS } from 'constants/route-constants';
import AppLayoutV2 from 'components/v2/app-layout/app-layout';
import AppBarV2 from 'components/v2/app-bar/app-bar';
import AppContentV2 from 'components/v2/app-content/app-content';
import Notification from 'components/v2/notification/notification';
import ErrorModal from 'components/v2/error-modal/error-modal';
import {
  DeliveryDetailOverlay,
  BulkEditOverlay,
  AddressDetailOverlay,
} from 'components/v2/anytime-booking/overlay';
import DeliveryConfigListener from 'components/v2/anytime-booking/delivery-config-listener';
import ContactHelperCenterIcon from 'views/v2/contact-help-center/contact-help-center.jsx';
import { isProduction } from 'common/utils/environment-util';
import { PROD_SIGNUP_LINK, STG_SIGNUP_LINK } from 'constants/app-constants';
import Routes from './RoutesV2';
import {
  ShopifyHome,
  ShopifySettings,
  ShopifyLink,
} from './views/v2/shopify-home';
import FailedPayments from './components/v2/failed-payments/failed-payments';
import UserLocationDetector from './components/v2/anytime-booking/user-location';
import SelectedCityListener from './components/v2/selected-city-listener';
import SuggestPOIListener from './components/v2/anytime-booking/listener/suggest-poi/suggest-poi';
import SavedPOIsListener from './components/v2/anytime-booking/listener/saved-poi';
import { isAuthHintEmpty } from './helper/session-helper';
import CookieBanner from './components/v2/cookie-banner/CookieBanner';

const AppV2 = ({ enableShopifyOrders }) => {
  return (
    <Fragment>
      <Helmet
        title={I18n.t('website.title')}
        meta={[
          {
            name: 'description',
            content: I18n.t('website.description'),
          },
        ]}
      />
      <Switch>
        <Route
          key={Routes.login.key}
          path={Routes.login.path}
          component={Routes.login.component}
          exact
        />
        <Route
          key={Routes.signup.key}
          path={Routes.signup.path}
          render={() => {
            window.location.replace(
              isProduction() ? PROD_SIGNUP_LINK : STG_SIGNUP_LINK,
            );
            return null;
          }}
          exact
        />
        <Route
          key={Routes.verifyLKKP.key}
          path={Routes.verifyLKKP.path}
          component={Routes.verifyLKKP.component}
        />
        <Route
          key={Routes.redirectGfb.key}
          path={Routes.redirectGfb.path}
          component={Routes.redirectGfb.component}
          exact
        />
        <Route
          key={Routes.redirectGrabId.key}
          path={Routes.redirectGrabId.path}
          component={Routes.redirectGrabId.component}
          exact
        />
        <Route
          key={Routes.shopifyLanding.key}
          path={Routes.shopifyLanding.path}
          component={Routes.shopifyLanding.component}
          exact
        />
        <Route
          key={Routes.waybills.key}
          path={Routes.waybills.path}
          component={Routes.waybills.component}
          exact
        />
        <Route
          key={Routes.ovoCallback.key}
          path={Routes.ovoCallback.path}
          component={Routes.ovoCallback.component}
          exact
        />
        <Route
          key={Routes.loginSuccessRedirect.key}
          path={Routes.loginSuccessRedirect.path}
          component={Routes.loginSuccessRedirect.component}
          exact
        />
        {isAuthHintEmpty() && (
          <Route
            path="*"
            key={Routes.errors.key}
            component={Routes.errors.component}
          />
        )}
        <Route
          render={() => (
            <AppLayoutV2>
              <CookieBanner />
              <Notification />
              <ErrorModal />
              <AppBarV2 />
              <FailedPayments />
              <UserLocationDetector />
              <DeliveryDetailOverlay />
              <BulkEditOverlay />
              <AddressDetailOverlay />
              <SelectedCityListener />
              <SuggestPOIListener />
              <SavedPOIsListener />
              <DeliveryConfigListener />
              <ContactHelperCenterIcon />
              <AppContentV2>
                <Switch>
                  <Route
                    key={Routes.home.key}
                    path={Routes.home.path}
                    component={Routes.home.component}
                    exact
                  />
                  <Route
                    key={Routes.newBooking.key}
                    path={Routes.newBooking.path}
                    component={Routes.newBooking.component}
                    exact
                  />
                  <Route
                    key={Routes.bulkUpload.key}
                    path={Routes.bulkUpload.path}
                    component={Routes.bulkUpload.component}
                    exact
                  />
                  <Route
                    key={Routes.bookings.key}
                    path={Routes.bookings.path}
                    component={Routes.bookings.component}
                    exact
                  />
                  <Route
                    key={Routes.errors.key}
                    path={Routes.errors.path}
                    component={Routes.errors.component}
                    exact
                  />
                  <Route
                    key={Routes.shopifyHome.key}
                    path={Routes.shopifyHome.path}
                    render={() => {
                      if (!enableShopifyOrders)
                        return <Redirect to={ROUTES_V2_PATHS.home} />;
                      return <ShopifyHome />;
                    }}
                    exact
                  />
                  <Route
                    key={Routes.shopifySettings.key}
                    path={Routes.shopifySettings.path}
                    render={() => {
                      if (!enableShopifyOrders)
                        return <Redirect to={ROUTES_V2_PATHS.home} />;
                      return <ShopifySettings />;
                    }}
                    exact
                  />
                  <Route
                    key={Routes.shopifyLink.key}
                    path={Routes.shopifyLink.path}
                    render={() => {
                      if (!enableShopifyOrders)
                        return <Redirect to={ROUTES_V2_PATHS.home} />;
                      return <ShopifyLink />;
                    }}
                    exact
                  />
                </Switch>
              </AppContentV2>
            </AppLayoutV2>
          )}
        />
      </Switch>
    </Fragment>
  );
};

AppV2.propTypes = {
  enableShopifyOrders: PropTypes.bool.isRequired,
};

export const mapStateToProps = state => ({
  enableShopifyOrders: enableShopifyOrdersSelector(state),
});

export default withRouter(connect(mapStateToProps)(AppV2));
